import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { MdAddchart } from 'react-icons/md';
import { FaFilter, FaInfoCircle, FaCheckCircle, FaDownload } from 'react-icons/fa';
import moment from 'moment';
import { Button, FieldLabel } from '../../components/EntityList';

const HORIZONTE_OPTIONS = [
	{ value: '3', label: '3 meses' },
	{ value: '6', label: '6 meses' },
	{ value: '12', label: '12 meses' },
	{ value: 'custom', label: 'Personalizado' },
];

const MONEDA_OPTIONS = [
	{ value: 'all', label: 'Todas las monedas' },
	{ value: 'UYU', label: 'Pesos ($)' },
	{ value: 'USD', label: 'Dólares (US$)' },
];

function Segmented({ options, value, onChange, size = 'default' }) {
	return (
		<div className='tw:inline-flex tw:w-full tw:overflow-hidden tw:rounded-[7px] tw:border tw:border-border'>
			{options.map((option) => (
				<button
					key={option.value}
					type='button'
					onClick={() => onChange(option.value)}
					className={classnames(
						'tw:flex-1 tw:border-0 tw:font-medium tw:transition-colors tw:duration-100',
						size === 'sm' ? 'tw:px-2 tw:py-1.5 tw:text-[11.5px]' : 'tw:px-3 tw:py-2 tw:text-[13px]',
						option.value === value ? 'tw:bg-accent tw:text-white' : 'tw:bg-surface tw:text-ink-2 tw:hover:bg-surface-2',
					)}
				>
					{option.label}
				</button>
			))}
		</div>
	);
}

/**
 * Sticky right-column filter panel. Body swaps between idle (filters),
 * preparing (spinner) and ready (download) — the same shape as Reports'
 * ReportConfigPanel.
 */
function PredictionFilterPanel({
	type,
	filters,
	dateErrors,
	onFilterChange,
	onClear,
	genState,
	genResult,
	onGenerate,
	onBack,
	onDownload,
}) {
	const minDate = moment.utc().startOf('month').format('YYYY-MM-DD');

	return (
		<div className='tw:rounded-panel tw:border tw:border-border tw:bg-surface'>
			<div className='tw:flex tw:items-center tw:gap-2 tw:border-b tw:border-border tw:px-5 tw:py-4'>
				<FaFilter className='tw:text-ink-2' size={15} />
				<h3 className='tw:m-0 tw:text-[14px] tw:font-semibold tw:text-ink-0'>Filtrar por</h3>
			</div>

			{genState === 'preparing' && (
				<div className='tw:flex tw:flex-col tw:items-center tw:gap-1 tw:px-5 tw:py-11 tw:text-center'>
					<div className='tw:mb-3 tw:h-[50px] tw:w-[50px] tw:animate-spin tw:rounded-full tw:border-[3px] tw:border-surface-3 tw:border-t-accent' />
					<div className='tw:font-semibold tw:text-ink-0'>Generando previsión…</div>
					<div className='tw:text-[12px] tw:text-ink-3'>Proyectando flujo por mes.</div>
				</div>
			)}

			{genState === 'ready' && (
				<div className='tw:flex tw:flex-col tw:gap-4 tw:px-5 tw:py-9'>
					<div className='tw:flex tw:flex-col tw:items-center tw:gap-1 tw:text-center'>
						<div className='tw:mb-2 tw:flex tw:h-[52px] tw:w-[52px] tw:items-center tw:justify-center tw:rounded-full tw:bg-ok-soft tw:text-ok tw:text-[22px]'>
							<FaCheckCircle />
						</div>
						<div className='tw:text-[14.5px] tw:font-semibold tw:text-ink-0'>Previsión generada</div>
						<div className='tw:font-mono tw:text-[12px] tw:text-ink-3'>{genResult?.filename}</div>
					</div>
					<div className='tw:flex tw:gap-2'>
						<Button variant='default' className='tw:flex-1 tw:justify-center' onClick={onBack}>
							Volver
						</Button>
						<Button variant='primary' icon={<FaDownload />} className='tw:flex-1 tw:justify-center' onClick={onDownload}>
							Descargar
						</Button>
					</div>
				</div>
			)}

			{genState === 'idle' && (
				<>
					<div className='tw:flex tw:flex-col tw:gap-[18px] tw:px-5 tw:py-[18px]'>
						{!type && (
							<div className='tw:flex tw:items-center tw:gap-2 tw:rounded-[7px] tw:border tw:border-border tw:bg-surface-2 tw:px-3 tw:py-2.5 tw:text-[11.5px] tw:text-ink-3'>
								<FaInfoCircle size={13} className='tw:shrink-0' />
								Elegí un tipo de previsión para habilitar la generación.
							</div>
						)}

						<div>
							<FieldLabel>Horizonte de proyección</FieldLabel>
							<Segmented
								options={HORIZONTE_OPTIONS}
								value={filters.horizonte}
								onChange={(value) => onFilterChange({ horizonte: value })}
							/>
						</div>

						{filters.horizonte === 'custom' && (
							<div className='tw:grid tw:grid-cols-2 tw:gap-2'>
								<div>
									<FieldLabel htmlFor='pred-date-from'>Desde</FieldLabel>
									<input
										id='pred-date-from'
										type='date'
										min={minDate}
										value={filters.dateFrom || ''}
										onChange={(e) => onFilterChange({ dateFrom: e.target.value })}
										className='tw:h-[34px] tw:w-full tw:rounded-[7px] tw:border tw:border-border-strong tw:bg-surface tw:px-2.5 tw:text-[13px] tw:text-ink-0'
									/>
									{dateErrors?.dateFrom && <div className='tw:mt-1 tw:text-[11px] tw:text-danger'>{dateErrors.dateFrom}</div>}
								</div>
								<div>
									<FieldLabel htmlFor='pred-date-to'>Hasta</FieldLabel>
									<input
										id='pred-date-to'
										type='date'
										min={minDate}
										value={filters.dateTo || ''}
										onChange={(e) => onFilterChange({ dateTo: e.target.value })}
										className='tw:h-[34px] tw:w-full tw:rounded-[7px] tw:border tw:border-border-strong tw:bg-surface tw:px-2.5 tw:text-[13px] tw:text-ink-0'
									/>
									{dateErrors?.dateTo && <div className='tw:mt-1 tw:text-[11px] tw:text-danger'>{dateErrors.dateTo}</div>}
								</div>
							</div>
						)}

						<div>
							<FieldLabel htmlFor='pred-moneda'>Moneda</FieldLabel>
							<select
								id='pred-moneda'
								value={filters.moneda}
								onChange={(e) => onFilterChange({ moneda: e.target.value })}
								className='formkit-select tw:h-[34px] tw:w-full tw:rounded-[7px] tw:border tw:border-border tw:bg-surface tw:px-2.5 tw:text-[13.5px] tw:text-ink-0'
							>
								{MONEDA_OPTIONS.map((option) => (
									<option key={option.value} value={option.value}>{option.label}</option>
								))}
							</select>
						</div>
					</div>

					<div className='tw:flex tw:items-center tw:justify-between tw:gap-2 tw:border-t tw:border-border tw:px-5 tw:py-3.5'>
						<Button variant='ghost' onClick={onClear}>Limpiar filtro</Button>
						<Button
							variant='primary'
							icon={<MdAddchart size={14} />}
							disabled={!type}
							className={!type ? 'tw:opacity-50 tw:cursor-not-allowed' : undefined}
							onClick={onGenerate}
						>
							Generar reporte
						</Button>
					</div>
				</>
			)}
		</div>
	);
}

PredictionFilterPanel.propTypes = {
	type: PropTypes.object,
	filters: PropTypes.shape({
		horizonte: PropTypes.string.isRequired,
		moneda: PropTypes.string.isRequired,
		dateFrom: PropTypes.string,
		dateTo: PropTypes.string,
	}).isRequired,
	dateErrors: PropTypes.object,
	onFilterChange: PropTypes.func.isRequired,
	onClear: PropTypes.func.isRequired,
	genState: PropTypes.oneOf(['idle', 'preparing', 'ready']).isRequired,
	genResult: PropTypes.shape({ filename: PropTypes.string }),
	onGenerate: PropTypes.func.isRequired,
	onBack: PropTypes.func.isRequired,
	onDownload: PropTypes.func.isRequired,
};

export default PredictionFilterPanel;
